import React, { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, FileSpreadsheet, CheckCircle2, AlertCircle, Search } from 'lucide-react';
import { Helmet } from 'react-helmet';
import SubaccountSwitcher from '@/components/SubaccountSwitcher';
import SingleFileUpload from '@/components/salesAudit/SingleFileUpload';
import MultiFileUpload from '@/components/salesAudit/MultiFileUpload';
import SalesAuditDashboard from '@/components/salesAudit/SalesAuditDashboard';
import { useSalesAudit } from '@/hooks/salesAudit/useSalesAudit';

const SalesAuditPage = ({ onClose }) => {
  const { user, activeAccountId } = useAuth();
  const { toast } = useToast();
  const {
    erpData,
    erpName,
    operatorFiles,
    auditResult,
    loading,
    uploadERP,
    addOperatorFile,
    removeOperatorFile,
    runAudit,
    clearAll,
  } = useSalesAudit();

  const [step, setStep] = useState('upload'); // 'upload' ou 'dashboard'

  const handleERPUpload = useCallback(async (file) => {
    try {
      const result = await uploadERP(file);
      toast({
        title: "Sucesso",
        description: `Relatório "${result.name}" carregado: ${result.count} vendas encontradas.`,
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao processar o relatório do sistema interno",
        variant: "destructive",
      });
    }
  }, [uploadERP, toast]);

  const handleAddOperatorFile = useCallback(async (file) => {
    try {
      const result = await addOperatorFile(file);
      toast({
        title: "Sucesso",
        description: `${result.operatorName}: ${result.count} vendas carregadas.`,
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao processar arquivo da maquininha",
        variant: "destructive",
      });
    }
  }, [addOperatorFile, toast]);

  const handleRemoveOperatorFile = useCallback((index) => {
    const removedName = operatorFiles[index]?.fileName || 'Arquivo';
    removeOperatorFile(index);
    toast({
      title: "Arquivo removido",
      description: `"${removedName}" foi removido da auditoria.`,
    });
  }, [removeOperatorFile, operatorFiles, toast]);

  const handleAudit = useCallback(() => {
    if (!erpData || erpData.length === 0) {
      toast({
        title: "Atenção",
        description: "Por favor, envie o relatório do sistema interno primeiro.",
        variant: "destructive",
      });
      return;
    }

    if (operatorFiles.length === 0) {
      toast({
        title: "Atenção",
        description: "Por favor, envie pelo menos um extrato de maquininha.",
        variant: "destructive",
      });
      return;
    }

    try {
      runAudit();
      setStep('dashboard');
      toast({
        title: "Sucesso",
        description: "Auditoria realizada com sucesso!",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao realizar auditoria",
        variant: "destructive",
      });
    }
  }, [erpData, operatorFiles, runAudit, toast]);

  const handleReset = useCallback(() => {
    clearAll();
    setStep('upload');
    toast({
      title: "Reset",
      description: "Dados limpos. Você pode fazer uma nova auditoria.",
    });
  }, [clearAll, toast]);

  return (
    <>
      <Helmet>
        <title>Auditoria de Vendas - Conciliador de Cartões</title>
        <meta name="description" content="Audite vendas do sistema interno com extratos das maquininhas." />
      </Helmet>

      <div className="min-h-screen text-white p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-gray-900 to-slate-800">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <header className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">
                📊 Auditoria de Vendas
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <SubaccountSwitcher />
            </div>
          </header>

          {step === 'upload' ? (
            <div className="space-y-6">
              {/* Cards de Upload */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Lado Esquerdo - Sistema Interno (ERP) */}
                <Card className="glass-effect border-white/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileSpreadsheet className="w-5 h-5 text-green-400" />
                      Vendas do Sistema Interno
                    </CardTitle>
                    <p className="text-sm text-white/60">
                      Relatório de vendas do PDV / ERP do cliente
                    </p>
                  </CardHeader>
                  <CardContent>
                    <SingleFileUpload
                      onUpload={handleERPUpload}
                      loading={loading}
                      uploaded={erpData && erpData.length > 0}
                      count={erpData?.length || 0}
                      label="Arraste o relatório do sistema interno"
                    />
                  </CardContent>
                </Card>

                {/* Lado Direito - Maquininhas */}
                <Card className="glass-effect border-white/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileSpreadsheet className="w-5 h-5 text-purple-400" />
                      Extratos das Maquininhas
                    </CardTitle>
                    <p className="text-sm text-white/60">
                      Envie um ou mais extratos de diferentes operadoras
                    </p>
                  </CardHeader>
                  <CardContent>
                    <MultiFileUpload
                      files={operatorFiles}
                      onAddFile={handleAddOperatorFile}
                      onRemoveFile={handleRemoveOperatorFile}
                      loading={loading}
                    />
                  </CardContent>
                </Card>
              </div>

              {/* Botão de Auditar */}
              {erpData && erpData.length > 0 && operatorFiles.length > 0 && (
                <div className="flex justify-center">
                  <Button
                    onClick={handleAudit}
                    disabled={loading}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 px-8 py-6 text-lg"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Processando...
                      </>
                    ) : (
                      <>
                        <Search className="w-5 h-5 mr-2" />
                        Auditar Vendas
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Instruções */}
              <Card className="glass-effect border-white/20">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                    <div className="text-sm text-white/70">
                      <p className="font-semibold text-white mb-2">Instruções:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>No lado esquerdo, envie o relatório de vendas do sistema interno do cliente (PDV/ERP)</li>
                        <li>No lado direito, envie os extratos de vendas das maquininhas (Stone, Cielo, Cappta, etc.)</li>
                        <li>Você pode enviar múltiplos extratos de maquininhas diferentes</li>
                        <li>O sistema comparará vendas por Data e Valor automaticamente</li>
                        <li>Após a auditoria, você verá quais vendas foram encontradas e quais estão faltando</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <SalesAuditDashboard
              auditResult={auditResult}
              onReset={handleReset}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default SalesAuditPage;
